/**
 * Action Dependencies
 */
import http from '../../../../services/HttpService'
import RandomString from 'random-string'
import { cloneDeep, get, has } from 'lodash'
import {
    saveProject,
    updateProject
} from '../../../../services/ProjectService'

/**
 * Select a project options
 *
 * @param  {Function} options.commit
 * @param  {Object}
 * @return {Void}
 */
const selectOption = ({ commit }, payload) => commit('PROJECT_MAKE_SELECTION', payload)

/**
 * Update form tab
 *
 * @param  {Function} options.commit
 * @param  {String} tab
 * @return {Void}
 */
const changeFormTab = ({ commit, rootState, dispatch }, tab) => {
    commit('CHANGE_FORM_TAB', tab)
    if (rootState.route.name === 'projects.create') {
        dispatch('saveDraft')
    }
}

/**
 * Reset the project choice context
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const projectChoicesReset = ({ commit }, payload) => commit('PROJECT_CHOICES_RESET')

/**
 * Update a form value
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const updateFormValue = ({ commit }, payload) => commit('UPDATE_PROJECT_VALUE', payload)

/**
 * Add a repeatable value to the form
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const addRepeatableObject = ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
        commit('ADD_REPEATABLE_PROJECT_VALUE', payload)
        resolve()
    })
}

/**
 * Remove a repeater object from a given key
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const removeRepeatableObject = ({ commit, state }, payload) => commit('REMOVE_REPEATABLE_PROJECT_VALUE', payload)

/**
 * Update the position of an item within a repeater
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const updateRepeatableObjectPosition = ({ commit, state }, payload) => {
    commit('UPDATE_REPEATABLE_PROJECT_VALUE_POSITION', payload)
    let errors = get(state.errors, payload.base, false)
    if (errors) {
        let errorIndex1 = errors.findIndex(item => item.index === payload.oldIndex)
        let errorIndex2 = errors.findIndex(item => item.index === payload.newIndex)
        if (errorIndex1 > -1) {
            commit('UPDATE_ERROR_INDEX', {
                base: payload.base,
                currentIndex: errorIndex1,
                newIndex: payload.newIndex
            })
        }
        if (errorIndex2 > -1) {
            commit('UPDATE_ERROR_INDEX', {
                base: payload.base,
                currentIndex: errorIndex2,
                newIndex: payload.oldIndex
            })
        }
    }
}

/**
 * Update a value inside a repeatable object
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const updateRepeatableObjectValue = ({ commit }, payload) => commit('UPDATE_REPEATABLE_OBJECT_VALUE', payload)

/**
 * Update a capex value
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const updateCapexValue = ({ commit }, payload) => commit('UPDATE_CAPEX_VALUE', payload)

/**
 * Trigger the form reset action
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const resetForm = ({ commit }) => commit('RESET_FORM')

const setUpProjectForm = ({ commit }, payload) => {
    commit('GET_PROJECT_FORM')
    return new Promise((resolve, reject) => {
        commit('SET_PROJECT_FORM', payload)
        resolve()
    })
}

/**
 * Reset the user context to its original state
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const resetState = ({ commit }) => commit('RESET_STATE')

/**
 * Update relative amount values when a dependent object changes
 *
 * @param  {Function} options.commit
 * @param  {Object} options.state
 * @param  {Object} payload
 * @return {Void}
 */
const updateRelativeAmounts = ({ commit, state }, payload) => {
    for (let prop in state.data.capex) {
        if (state.data.capex[prop]) {
            state.data.capex[prop].forEach((item, capexItemIndex) => {
                let idx = item.relativity.relative_to.findIndex(relativeItem => relativeItem.hash === payload.hash)
                if (idx > -1) {
                    commit('UPDATE_RELATIVE_VALUE', {
                        index: idx,
                        base: `capex.${prop}[${capexItemIndex}].relativity.relative_to`,
                        value: payload.newValue
                    })
                }
            })
        }
    }
}

/**
 * Submit the project form data to the API
 *
 * @param  {Function} options.commit
 * @param  {Object} options.state
 * @return {Void}
 */
const submitProjectForm = ({ commit, state, dispatch, rootState }) => new Promise((resolve, reject) => {
    commit('PROJECT_SAVE')
    saveProject(state.data)
        .then(res => {
            commit('PROJECT_SAVE_SUCCESS')
            resolve(res)
        })
        .catch(error => {
            let serverError = error.response.status === 500
            let message = serverError
                ? 'Something\'s gone wrong on our end. Please try again later or report this to a system administrator.'
                : 'There were problems with your project submission. Please make your corrections and resubmit your project.'
            dispatch('addNotification', {
                title: 'Error Creating Project',
                type: 'error',
                text: message,
                timeout: true
            }, { root: true })
            commit('PROJECT_SAVE_FAILURE', serverError ? {} : error.response.data)
            reject(error)
        })
})

/**
 * Attempt to update an existing project
 *
 * @param  {Function} options.state
 * @param  {Object} options.dispatch
 * @param  {Number} id
 * @return {Promise}
 */
const submitProjectEditForm = ({ commit, state, dispatch }, id) => {
    commit('PROJECT_SAVE')
    return updateProject(id, state.data)
        .then(res => {
            commit('PROJECT_SAVE_SUCCESS')
            return Promise.resolve(res.data)
        })
        .catch(error => {
            let serverError = error.response.status === 500
            let message = serverError
                ? 'Something\'s gone wrong on our end. Please try again later or report this to a system administrator.'
                : 'There were problems with your project submission. Please make your corrections and resubmit your project.'
            dispatch('addNotification', {
                title: 'Error Creating Project',
                type: 'error',
                text: message,
                timeout: true
            }, { root: true })
            commit('PROJECT_SAVE_FAILURE', serverError ? {} : error.response.data)
            return Promise.reject(error)
        })
}

/**
 * Add a new debt object
 *
 * @param  {Function} options.commit
 * @param  {Object} options.state
 * @param  {Object} payload
 * @return {Void}
 */
const addNewDebt = ({ commit }, payload) => commit('ADD_NEW_DEBT', payload)

/**
 * Update an existing debt cost
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const updateExistingDebt = ({ commit }, payload) => commit('UPDATE_EXISTING_DEBT', payload)

/**
 * Update an existing CAPEX object
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const updateExistingCapexObject = ({ commit }, payload) => commit('UPDATE_EXISTING_CAPEX', payload)

/**
 * Add a new equity object to the state
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const addNewEquity = ({ commit }, payload) => commit('ADD_NEW_EQUITY', payload)

/**
 * Remove and equity object
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const removeExistingEquity = ({ commit }, payload) => commit('REMOVE_EQUITY', payload)

/**
 * Add a new equity object to the state
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const updateExistingEquity = ({ commit }, payload) => commit('UPDATE_EXISTING_EQUITY', payload)

/**
 * Remove a key from the errors object
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const clearFormError = ({ commit }, payload) => commit('REMOVE_ERROR_KEY', payload)

/**
 * Copy an existing capex and insert it the clone after the original
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const copyAndInsertCapex = ({ commit, dispatch }, payload) => new Promise((resolve, reject) => {
    let clone = cloneDeep(payload.capex)
    commit('SET_CAPEX_LOADING')
    clone.name = clone.name + ' copy'
    clone.amount = 0
    clone.id = null
    clone.hash = RandomString()
    dispatch('projects/form/capex/createCost', clone, { root: true })
        .then(item => {
            commit('ADD_CAPEX_CLONE', {
                clone: item,
                index: payload.index + 1,
                type: payload.type
            })
            dispatch('setCapexOrder')
            dispatch('addNotification', {
                title: 'Copy successful',
                type: 'success',
                text: 'The capital expense has been successfully copied.',
                timeout: true
            }, { root: true })
            commit('RESET_CAPEX_LOADING')
            resolve({
                id: item.id,
                index: payload.index + 1
            })
        })
        .catch(err => {
            commit('RESET_CAPEX_LOADING')
            dispatch('addNotification', {
                title: 'Copy Failed',
                type: 'error',
                text: 'There was a problem creating a copy of the capital expense.',
                timeout: true
            }, { root: true })
            reject(err)
        })
})

/**
 * Set the edit payload
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const setProjectData = ({ commit }, payload) => commit('SET_EDIT_PAYLOAD', payload)

/**
 * Remove a capex item and remove the references to it from associated relative costs
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const removeCapexObject = ({ commit, state }, payload) => {
    commit('REMOVE_REPEATABLE_PROJECT_VALUE', payload)
    let keys = Object.keys(state.data.capex)
    Object.values(state.data.capex).forEach(arr => {
        if (arr) {
            arr.forEach((capex, idx) => {
                if (capex.relativity.is_relative) {
                    let relativeIndex = capex.relativity.relative_to.map(o => o.hash).indexOf(payload.hash)
                    if (relativeIndex > -1) {
                        commit('REMOVE_DELETED_RELATIVE_VALUE', {
                            path: `data.capex.${keys[idx]}[${idx}].relativity.relative_to`,
                            index: relativeIndex
                        })
                    }
                }
            })
        }
    })
    if (has(state.errors, `capex.${payload.type}`)) {
        let idx = state.errors.capex[payload.type].findIndex(item => item.index === payload.index)
        if (idx > -1) {
            commit('REMOVE_CAPEX_ERROR', {
                type: payload.type,
                index: payload.index
            })
        }
    }
}

/**
 * Set the working capital model on the capex state
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const setWorkingCapitalModel = ({ commit }, form) => commit('SET_WORKING_CAPITAL_MODEL', JSON.parse(JSON.stringify(form)))

/**
 * Tear down the working captial model
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const removeWorkingCapitalModel = ({ commit }) => commit('REMOVE_WORKING_CAPITAL_MODEL')

/**
 * Trigger a replace of a repeater object
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const replaceRepeaterObject = ({ commit }, payload) => commit('REPLACE_REPEATER_OBJECT', payload)

/**
 * Trigger a replace of a repeater object
 *
 * @param  {Function} options.commit
 * @param  {Object|Null} payload
 * @return {Void}
 */
const setDebtObject = ({ commit }, payload) => commit('SET_DEBT_OBJECT', payload)

/**
 * Save a draft of the current project form
 *
 * @param  {Object} options.state
 * @return {Promise}
 */
const saveDraft = ({ commit, state }) => {
    commit('SAVING_DRAFT')
    return http.post('projects/draft/', state.data)
        .then(() => commit('RESET_SAVING_DRAFT'))
        .catch(() => commit('RESET_SAVING_DRAFT'))
}

/**
 * Restore the latest saved form draft
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const restoreDraft = ({ commit, dispatch }) => {
    commit('RESTORE_DRAFT')
    dispatch('addNotification', {
        title: 'Draft Restored',
        type: 'success',
        text: 'The project has been restored to the latest draft.',
        timeout: true
    }, { root: true })
}

/**
 * Get the draft state
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const getDraft = ({ commit }) => {
    http.get('projects/draft/')
        .then(res => commit('GET_DRAFT_SUCCESS', res.data))
        .catch(() => commit('GET_DRAFT_FAILURE'))
}

/**
 * Reset the success state
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const resetSuccessState = ({ commit }) => commit('RESET_SAVE_SUCCESS')

/**
 * Update the form's unit values dynamically
 *
 * @param  {Function} options.commit
 * @param  {Object} data
 * @return {Void}
 */
const updateUnitValues = ({ commit }, data) => commit('IMPORT_UNIT_VALUES', data)

/**
 * Update the opex via the importer
 *
 * @param  {Function} options.commit
 * @param  {Array} data
 * @return {Void}
 */
const ingestOpexValues = ({ commit }, data) => commit('INGEST_OPEX', data)

/*
 * Update either the rent roll or opex file ID
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const updateFileId = ({ commit }, payload) => commit('UPDATE_FILE_ID', payload)

/**
 * Update all AGR values in the rental income repeater
 *
 * @param  {Function} options.commit
 * @param  {Number} value
 * @return {void}
 */
const updateAllAGR = ({ commit }, value) => commit('UPDATE_ALL_AGR', value)

/**
 * Update all AGR values in the rental opex repeater
 *
 * @param  {Function} options.commit
 * @param  {Number} value
 * @return {void}
 */
const updateAllOpexAGR = ({ commit }, value) => new Promise((resolve, reject) => {
    commit('UPDATE_ALL_OPEX_AGR', value)
    resolve()
})

/**
 * Set the global form meta
 *
 * @param  {Function} options.commit
 * @param  {Object} meta
 * @return {Promise}
 */
const setFormMeta = ({ commit }, meta) => commit('SET_FORM_META', meta)

/**
 * Delete a capex object
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const deleteCapex = ({ commit, dispatch }, payload) => {
    commit('SET_CAPEX_LOADING')
    http.del(`modelling/capex/${payload.id}/`)
        .then(res => {
            commit('REMOVE_CAPEX', payload)
            commit('RESET_CAPEX_LOADING')
            dispatch('addNotification', {
                title: 'Success',
                type: 'success',
                text: res.data,
                timeout: true
            }, { root: true })
        })
        .catch(() => {
            commit('RESET_CAPEX_LOADING')
            dispatch('addNotification', {
                title: 'Failed',
                type: 'error',
                text: 'The capital expense failed to delete.',
                timeout: true
            }, { root: true })
        })
}

const updateCapex = ({ commit, dispatch }, payload) => new Promise((resolve, reject) => {
    http
        .put(`modelling/capex/${payload.id}/`, payload.data)
        .then(res => {
            commit('UPDATE_CAPEX', payload)
            dispatch('addNotification', {
                title: 'Success',
                type: 'success',
                text: 'Capex has been updated successfully.',
                timeout: true
            }, { root: true })
            resolve()
        })
        .catch(err => reject(err.response.data))
})

const replaceCapex = ({ commit }, payload) => commit('UPDATE_CAPEX', payload)

const setCapexOrder = ({ state, rootState }) => new Promise((resolve, reject) => {
    let order = Object.keys(state.data.capex).reduce((result, i) => {
        if (Array.isArray(state.data.capex[i])) {
            result[i] = state.data.capex[i].map(item => item.id)
        }
        return result
    }, {})
    http.put(`projects/${rootState.route.params.projectId}/capex/`, order)
        .then(res => resolve())
        .catch(() => resolve())
})

const replaceCapexRepeater = ({ commit }, payload) => commit('REPLACE_CAPEX_REPEATER', payload)

const addCapex = ({ commit }, payload) => commit('ADD_CAPEX', payload)

export default {
    addCapex,
    getDraft,
    saveDraft,
    setCapexOrder,
    replaceCapex,
    resetForm,
    addNewDebt,
    resetState,
    updateCapex,
    deleteCapex,
    setFormMeta,
    updateAllAGR,
    updateFileId,
    addNewEquity,
    selectOption,
    restoreDraft,
    changeFormTab,
    setDebtObject,
    clearFormError,
    setProjectData,
    updateFormValue,
    updateAllOpexAGR,
    ingestOpexValues,
    updateCapexValue,
    setUpProjectForm,
    updateUnitValues,
    resetSuccessState,
    submitProjectForm,
    removeCapexObject,
    copyAndInsertCapex,
    updateExistingDebt,
    addRepeatableObject,
    projectChoicesReset,
    updateExistingEquity,
    removeExistingEquity,
    replaceRepeaterObject,
    replaceCapexRepeater,
    submitProjectEditForm,
    updateRelativeAmounts,
    setWorkingCapitalModel,
    removeRepeatableObject,
    removeWorkingCapitalModel,
    updateExistingCapexObject,
    updateRepeatableObjectValue,
    updateRepeatableObjectPosition
}



// WEBPACK FOOTER //
// ./src/state/modules/projects/form/actions.js